// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import {
  activeControlAnswers,
  isContentControl,
  shopperContentAnswers,
} from '@theme/custom.customizer-content';
import { propertyName } from '@theme/custom.customizer-handle';
import { DESIGN_PROOF_PRICE_CENTS } from '@theme/custom.customizer-pricing';
import { presentmentCents } from '@theme/custom.customizer-currency';
import { isDeclineValue, isFaceModeControl } from '@theme/custom.customizer-store';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */
/** @typedef {import('@theme/custom.customizer-store').CustomizerStep} CustomizerStepDef */
/** @typedef {import('@theme/events').CustomizerUpdateEvent} CustomizerUpdateEvent */

/** The dataset title a scan upload's row takes; any other upload is a "Photo" row. */
const SCAN_TITLES = new Map([
  ['fingerprint', 'titleFingerprint'],
  ['handwriting', 'titleHandwriting'],
]);

/**
 * One rendered Review row.
 * @typedef {object} ReviewRow
 * @property {string} title - "Front Engraving" / "Left Photo" / "Birthstone" / a bare side label.
 * @property {number} priceCents - The step's display surcharge (0 renders no price).
 * @property {string[]} details - "Text: Emily"-style secondary lines.
 * @property {string} actionLabel - "Edit" (configured) or "Add" (empty).
 * @property {number} stepIndex - Target for the action's goTo jump (-1 renders no action).
 */

/**
 * The Review step (final confirmation before Add to Cart): side tabs (Front/Left/Right/Back) with
 * one tab ALWAYS selected, there is no overview state, switching the hero preview AND the summary
 * list. The primary (front) tab carries the whole-piece summary: the static options first (Metal
 * and Size variant rows, then the side-less personalization steps such as Chain/Birthstone, all in
 * schema order) with the face's own engraving row LAST. Every other tab shows just that side's row
 * (title + "+$X" + detail lines + Edit, or the empty-side row with an Add link). The collapsible
 * Special Instructions notes field sits below the list on EVERY tab. Rows are built client-side
 * from the store: everything shown traces to the product's `custom.customizer` schema plus the
 * shopper's inputs, so nothing here is product-specific. A front no step edits (a factory-finished
 * face) gets a read-only tab: the variant photo on the stage, the whole-piece rows below.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} rows - The summary-row list container (client-rendered).
 * @property {HTMLButtonElement[]} [tabs] - The side tabs: the read-only front where no step owns
 *   the front (`data-fixed-face`), then the face steps in schema order.
 * @property {HTMLButtonElement} [instructionsAdd] - The collapsed "Add Special Instructions" link.
 * @property {HTMLElement} [instructionsBody] - The expanded notes field + Cancel wrapper.
 *
 * @extends Component<Refs>
 */
class CustomizerReview extends Component {
  requiredRefs = ['rows'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.customizerUpdate, this.#handleUpdate);
    this.#renderFromHost();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#handleUpdate);
  }

  /** @param {Event} event */
  #handleUpdate = (event) => {
    const { configId } = /** @type {CustomizerUpdateEvent} */ (event).detail;
    if (configId !== this.#configId) return;
    this.#renderFromHost();
  };

  /** @returns {string} The owning customizer instance's config id (for scoping document events). */
  get #configId() {
    const host = this.closest('custom-customizer');
    return host instanceof HTMLElement ? (host.dataset.configId ?? '') : '';
  }

  /** @returns {string} The Review step's own id, read off the wizard panel this renders in. */
  get #stepId() {
    return this.closest('[data-customizer-step]')?.getAttribute('data-customizer-step') ?? '';
  }

  /** @returns {CustomizerStore | undefined} */
  get #store() {
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    return host?.store;
  }

  #renderQueued = false;

  /** Deferred a frame, never synchronous: replaceChildren during a row button's own click detaches
   *  the target, which dialog.js then reads as an outside click and closes the whole customizer. */
  #renderFromHost() {
    if (this.#renderQueued) return;
    this.#renderQueued = true;
    requestAnimationFrame(() => {
      this.#renderQueued = false;
      const store = this.#store;
      if (store) this.#render(store);
    });
  }

  /**
   * Side tab tap: route the hero preview AND the summary list to that side. One tab is always
   * selected (there is no overview state), so re-tapping the active tab is a no-op
   * ({@link CustomizerStore#setPreviewSide} ignores same-value writes).
   * @param {Event} event
   */
  selectSide(event) {
    const tab = /** @type {Element} */ (event.target).closest('[data-side]');
    if (!(tab instanceof HTMLElement)) return;
    const side = tab.dataset.side;
    if (side) this.#store?.setPreviewSide(side);
  }

  expandInstructions() {
    this.#setInstructionsOpen(true);
    const textarea = this.querySelector('.custom-customizer-control__textarea--notes');
    if (textarea instanceof HTMLTextAreaElement) textarea.focus();
  }

  /** Cancel discards the note: same transactional contract as the design-request drawer. */
  cancelInstructions() {
    const key = this.dataset.commentsKey;
    if (key) this.#store?.updateControl(key, '');
    this.#setInstructionsOpen(false);
    // Cancel sat in the body just hidden; hand focus back to the button that opens it.
    this.refs.instructionsAdd?.focus();
  }

  /** @param {boolean} open */
  #setInstructionsOpen(open) {
    this.refs.instructionsBody?.toggleAttribute('hidden', !open);
    this.refs.instructionsAdd?.toggleAttribute('hidden', open);
  }

  /** @param {CustomizerStore} store */
  #render(store) {
    if (store.isEmpty) return;
    const steps = store.configSchema?.steps ?? [];
    const order = store.stepOrder;

    // A face no step edits keeps its tab only while the store has a frame for it: the
    // variant photo, or a render some control draws on. It is the first tab, so it leads below.
    const tabs = this.refs.tabs ?? [];
    for (const tab of tabs) {
      if (!('fixedFace' in tab.dataset)) continue;
      tab.toggleAttribute('hidden', !store.showsFixedFace(tab.dataset.side ?? ''));
    }

    // One tab is always selected, so default to the first face and commit it as the store's preview
    // override, but only while Review is active: otherwise it would repoint another step's hero.
    const primarySide =
      tabs.find((tab) => !tab.hidden)?.dataset.side ??
      steps.find((step) => step.side)?.side ??
      null;
    const selectedSide = store.previewSide ?? primarySide;
    if (!store.previewSide && selectedSide && store.currentStep === this.#stepId) {
      store.setPreviewSide(selectedSide);
    }

    for (const tab of tabs) {
      tab.setAttribute('aria-pressed', String(tab.dataset.side === selectedSide));
    }

    /** @type {ReviewRow[]} */
    const rows = [];

    // A side-less product has no tabs, so always render its whole-piece summary.
    // Comments render below the list; also skip this panel when it has no controls.
    if (!primarySide || (selectedSide && selectedSide === primarySide)) {
      const selfId = this.#stepId;
      for (const step of steps) {
        if (step.side) continue;
        if (step.id === selfId) continue;
        if (step.controls?.some((control) => control.type === 'comments')) continue;
        const stepIndex = order.indexOf(step.id);
        if (step.controls?.some((control) => control.type === 'variant')) {
          rows.push(this.#variantRow(step, stepIndex, store));
        } else {
          rows.push(this.#globalRow(step, stepIndex, store));
        }
      }
    }
    for (const step of steps) {
      if (!step.side || step.side !== selectedSide) continue;
      const stepIndex = order.indexOf(step.id);
      // A face step that only asks choices (a raised design set) holds no engraving, and the
      // engraving row reads none of its picks; it summarizes like a side-less step.
      const controls = step.controls ?? [];
      const picksOnly =
        !controls.some(isContentControl) && controls.some((control) => control.type === 'choice');
      rows.push(
        picksOnly ? this.#globalRow(step, stepIndex, store) : this.#sideRow(step, stepIndex, store)
      );
    }

    this.refs.rows.replaceChildren(...rows.map((row) => this.#buildRow(row)));

    // A saved note must always be visible for review, so force the field open when it holds one.
    const key = this.dataset.commentsKey;
    const note = key ? store.getControlValue(key) : null;
    if (typeof note === 'string' && note !== '') this.#setInstructionsOpen(true);
  }

  /**
   * A face's row: "«Side» Engraving/Photo" + the step surcharge + what's on it — or, when the
   * side is empty, the bare side label with "No photo or engraving" and an Add link. Only the
   * selected tab's side renders (the caller filters), so the empty state always belongs to the
   * face the shopper is looking at.
   * @param {CustomizerStepDef} step
   * @param {number} stepIndex
   * @param {CustomizerStore} store
   * @returns {ReviewRow}
   */
  #sideRow(step, stepIndex, store) {
    const sideLabel = this.#sideLabel(step);
    /** @type {string[]} */
    const details = [];
    const content = shopperContentAnswers(step, store);
    let hasPhoto = false;
    let hasEngraving = false;
    let photoTitle = 'titlePhoto';

    for (const { control, value } of content) {
      if (control.type === 'upload') {
        hasPhoto = true;
        // Presentation only: a scan titles the row "Fingerprint" or "Handwriting" instead of
        // "Photo". uploadKind is product data, never a guess here.
        photoTitle = SCAN_TITLES.get(control.uploadKind ?? '') ?? photoTitle;
        const file = store.getControlFile(control.key);
        if (file) details.push(`${this.dataset.labelImage}: ${file.name}`);
        // The proof's "+$X" is already folded into the row surcharge (store.stepSurchargeCents),
        // so this line itemizes that amount rather than adding to it. Same gate as the billing.
        const request = store.getDesignRequest(control.key);
        if (request?.note) details.push(`${this.dataset.labelNotes}: ${request.note}`);
        if (request?.proof) details.push(this.#proofDetail());
      } else if (control.type === 'text') {
        hasEngraving = true;
        details.push(`${this.dataset.labelText}: ${value.replace(/\n+/g, ' / ').trim()}`);
      } else if (control.type === 'monogram') {
        hasEngraving = true;
        details.push(`${this.dataset.labelMonogram}: ${value}`);
      }
      // clipart: the value is an art URL with no human-readable label in the schema, so the row
      // shows (title + Edit) with no detail line.
    }

    // Surface the picked Select Style: a restored photo keeps only its object-URL value, not the
    // File name, so without this the row can show no detail at all. Mode controls are skipped.
    const areas = store.faceAreas(step.id);
    if (hasPhoto) {
      // A face with several masks keeps the text mask's font choice out of the photo Style line.
      const methods =
        areas.length > 1 ? new Set(areas.map((plan) => plan.method?.key).filter(Boolean)) : null;
      for (const control of step.controls ?? []) {
        if (control.type !== 'choice' || isFaceModeControl(control)) continue;
        if (methods && !methods.has(control.key)) continue;
        if (!store.isControlActive(control.key)) continue;
        const value = store.getControlValue(control.key);
        if (typeof value !== 'string' || value === '') continue;
        const option = control.options?.find((candidate) => candidate.value === value);
        if (option?.label) {
          // Break after the first: the method is the only active non-mode choice in Photo mode, and
          // stopping keeps a single "Style:" line if a future config leaves another one active.
          details.push(`${this.dataset.labelStyle}: ${option.label}`);
          break;
        }
      }
    }

    if (content.length === 0) {
      return {
        title: sideLabel,
        priceCents: 0,
        details: [this.dataset.labelEmpty ?? ''],
        actionLabel: this.dataset.labelAdd ?? '',
        stepIndex,
      };
    }

    // Separate masks can fill one face with a photo and engraving together; one mask cannot.
    const both = hasPhoto && hasEngraving && areas.length > 1;
    const photoTemplate = this.dataset[both ? 'titlePhotoEngraving' : photoTitle];
    const template = (hasPhoto ? photoTemplate : this.dataset.titleEngraving) ?? '[side]';
    return {
      title: template.replace('[side]', () => sideLabel),
      priceCents: store.stepSurchargeCents(step.id),
      details,
      actionLabel: this.dataset.labelEdit ?? '',
      stepIndex,
    };
  }

  /**
   * A variant-axis step's row (Metal/Size): the step's schema label + the committed variant option
   * value ("14k Yellow Gold") with no surcharge badge (variant price deltas ride the footer total,
   * not a "+$X"). The value reads the step state's `userInput`: what setVariant commits (the
   * option-value label from `variantAxes`) and what a single-value axis is seeded with in the store
   * constructor. A seeded (hidden) axis is kept out of the navigation order, so its stepIndex is -1
   * and the row renders value-only with no Edit link.
   * @param {CustomizerStepDef} step
   * @param {number} stepIndex
   * @param {CustomizerStore} store
   * @returns {ReviewRow}
   */
  #variantRow(step, stepIndex, store) {
    const value = store.steps[step.id]?.userInput;
    const label = typeof value === 'string' && value !== '' ? value : null;
    /** @type {string[]} */
    const details = [];
    if (label) details.push(label);
    else if (this.dataset.labelNone) details.push(this.dataset.labelNone);
    return {
      title: step.label ?? step.id,
      priceCents: 0,
      details,
      actionLabel: (label ? this.dataset.labelEdit : this.dataset.labelAdd) ?? '',
      stepIndex,
    };
  }

  /**
   * A side-less personalization step's row (birthstone, chain, and any `customize` step — rendered
   * on the primary/front tab only): the step label + one detail line per answered control. Choices
   * read as "Type: «option»"; typed content reads as "«control label»: «value»". Unconfigured steps
   * keep a row with an Add link so the shopper can still reach them from Review (the side tabs'
   * empty-state affordance, applied here) — but a step IS configured as soon as any of its controls
   * holds a value, whatever its type.
   * @param {CustomizerStepDef} step
   * @param {number} stepIndex
   * @param {CustomizerStore} store
   * @returns {ReviewRow}
   */
  #globalRow(step, stepIndex, store) {
    // The chain step carries no schema control: its selection lives in `store.chain`, not the
    // per-control map, so the choice loop below cannot see it. Route it to its own reader.
    if (step.unresolvedRef?.type === 'chain_group') return this.#chainRow(step, stepIndex, store);

    /** @type {string[]} */
    const details = [];
    // A generic "Type:" lead reads right only when the step asks ONE question, and a birthstone
    // step routinely asks several, so with 2+ choices each line wears its own label instead.
    const choiceCount = (step.controls ?? []).filter((control) => control.type === 'choice').length;
    for (const { control, value } of activeControlAnswers(step, store)) {
      // The Review step's own notes field renders below the list, never as a row detail.
      if (control.type === 'comments') continue;
      const name = propertyName(control.label);

      if (control.type === 'choice') {
        if (isDeclineValue(value)) continue;
        const option = control.options?.find((candidate) => candidate.value === value);
        const lead = choiceCount > 1 && name ? name : this.dataset.labelType;
        details.push(`${lead}: ${option?.label ?? value}`);
        continue;
      }
      // A side-less step can hold text fields, a monogram or clip-art alongside its choices. Each
      // line wears its control's own label, so a multi-field step says which answer is which.
      if (!isContentControl(control)) continue;
      const text =
        control.type === 'upload' ? (store.getControlFile(control.key)?.name ?? '') : value;
      if (text.trim() === '') continue;
      // A multi-line text value arrives newline-joined; the row is one line of summary text.
      const flat = text.replace(/\s*\n\s*/g, ' ').trim();
      details.push(name ? `${name}: ${flat}` : flat);
    }

    const configured = details.length > 0;
    if (!configured && this.dataset.labelNone) details.push(this.dataset.labelNone);
    return {
      title: step.label ?? step.id,
      priceCents: configured ? store.stepSurchargeCents(step.id) : 0,
      details,
      actionLabel: (configured ? this.dataset.labelEdit : this.dataset.labelAdd) ?? '',
      stepIndex,
    };
  }

  /**
   * The chain step's row: the structured selection lives in `store.chain`, not a control, so
   * it's read here rather than through the choice loop. Detail mirrors the birthstone's "Type: …"
   * line but joins the chain's descriptive attributes: metal, style, size (e.g. "Type: Sterling
   * Silver, Box, Adjustable"). The price is the step's own surcharge (the chain is
   * full-price-on-the-row, shown as "+$99" per the design). An unpicked/skipped chain keeps a row
   * with an Add link so the shopper can still reach the step from Review.
   * @param {CustomizerStepDef} step
   * @param {number} stepIndex
   * @param {CustomizerStore} store
   * @returns {ReviewRow}
   */
  #chainRow(step, stepIndex, store) {
    const chain = store.chain;
    /** @type {string[]} */
    const details = [];
    if (chain) {
      const parts = [chain.metal, chain.option, chain.length].filter(Boolean);
      if (parts.length > 0) details.push(`${this.dataset.labelType}: ${parts.join(', ')}`);
    } else if (this.dataset.labelNone) {
      details.push(this.dataset.labelNone);
    }

    const configured = chain !== null;
    return {
      title: step.label ?? step.id,
      priceCents: configured ? store.stepSurchargeCents(step.id) : 0,
      details,
      actionLabel: (configured ? this.dataset.labelEdit : this.dataset.labelAdd) ?? '',
      stepIndex,
    };
  }

  /**
   * The short tab label for a side step ("Left", not "Left Inside") — read from the step's own
   * server-rendered tab, so JS and Liquid can't drift.
   * @param {CustomizerStepDef} step
   * @returns {string}
   */
  #sideLabel(step) {
    const tab = (this.refs.tabs ?? []).find((candidate) => candidate.dataset.side === step.side);
    return tab?.textContent?.trim() || step.label || step.id;
  }

  /**
   * @param {ReviewRow} row
   * @returns {HTMLElement}
   */
  #buildRow(row) {
    const root = document.createElement('div');
    root.className = 'custom-customizer-review__row';

    const content = document.createElement('div');
    content.className = 'custom-customizer-review__row-content';

    const head = document.createElement('span');
    head.className = 'custom-customizer-review__row-head';
    const title = document.createElement('span');
    title.className = 'custom-customizer-review__row-title';
    title.textContent = row.title;
    head.appendChild(title);
    if (row.priceCents > 0) {
      const price = document.createElement('span');
      price.className = 'custom-customizer-review__row-price';
      price.textContent = `${this.dataset.surchargePrefix ?? '+'}${this.#formatPrice(row.priceCents)}`;
      head.appendChild(price);
    }
    content.appendChild(head);

    for (const detail of row.details) {
      if (!detail) continue;
      const line = document.createElement('span');
      line.className = 'custom-customizer-review__row-detail';
      line.textContent = detail;
      content.appendChild(line);
    }
    root.appendChild(content);

    if (row.stepIndex >= 0 && row.actionLabel) {
      const action = document.createElement('button');
      action.type = 'button';
      action.className = 'custom-customizer-review__row-action button-unstyled';
      action.textContent = row.actionLabel;
      action.setAttribute('on:click', `custom-customizer/goTo?index=${row.stepIndex}`);
      root.appendChild(action);
    }
    return root;
  }

  /**
   * The Request Proof detail line for a photo face that opted in: "Proof requested (+$10)",
   * mirroring the Edit Photo chip's line (custom.customizer-engrave.js). The label is the shared
   * `design_request_proof_requested` string; the amount comes from {@link DESIGN_PROOF_PRICE_CENTS}
   * (the single source) formatted like every other row surcharge, so it reads "+$10" not "+$10.00".
   * @returns {string}
   */
  #proofDetail() {
    const label = this.dataset.labelProof ?? '';
    const proofCents = presentmentCents(DESIGN_PROOF_PRICE_CENTS);
    const price = `${this.dataset.surchargePrefix ?? '+'}${this.#formatPrice(proofCents)}`;
    return `${label} (${price})`.trim();
  }

  /**
   * Format a row surcharge. Trailing ".00" is stripped to match the option badges ("+$10"), which
   * render via Liquid's `money_without_trailing_zeros` (custom.customizer-surcharge.liquid).
   * @param {number} cents - Already in the shopper's currency.
   * @returns {string}
   */
  #formatPrice(cents) {
    const shell = this.closest('custom-customizer-step');
    const format = shell instanceof HTMLElement ? shell.dataset.moneyFormat : undefined;
    const currency = shell instanceof HTMLElement ? shell.dataset.currency : undefined;
    const money = formatMoney(cents, format || '{{amount}}', currency || 'USD');
    return money.replace(/([.,]00)(?=\D*$)/, '');
  }
}

if (!customElements.get('custom-customizer-review')) {
  customElements.define('custom-customizer-review', CustomizerReview);
}
